import { useParams, Link } from 'wouter-preact';
import { ArrowLeft } from 'lucide-preact';
import type { Analysis } from '@teacher-shared/analysis';
import { StudentTabs } from './StudentProgress';
import { TeachingPage, Card, StatusChip, PerfChip, RateBar, INK, perfFill, pct, useClass, WithData, useAnalysis, ProvisionalBanner, breadcrumbOf, teachHref, CodeTag } from '@/components/teaching/ui';

const GROUP_LABEL = { reteach: 'Whole-class reteach', small: 'Small group', individual: 'One-to-one', extension: 'Extension' } as const;

function DraftChip({ label }: { label: string }) {
  return <span class="inline-flex items-center px-1.5 py-0.5 rounded text-[10.5px] font-medium whitespace-nowrap" style={{ background: '#fde9b8', color: INK }}>{label}</span>;
}

function ReviewChip() {
  return <span class="inline-flex items-center px-1.5 py-0.5 rounded text-[10.5px] font-medium whitespace-nowrap" style={{ background: '#fddcbf', color: INK }}>Needs re-check</span>;
}

export function StudentDetail() {
  const params = useParams<{ assessmentId: string; id: string }>();
  return (
    <WithData state={useAnalysis(params.assessmentId)} title="Student">
      {({ a, A }) => <StudentView a={a} A={A} id={params.id} />}
    </WithData>
  );
}

function StudentView({ a, A, id }: { a: string; A: Analysis; id: string }) {
  const [cls] = useClass(A.classes);
  const student = id ? A.studentById[id] : undefined;
  const sum = student ? A.summaryOf(student.id) : undefined;

  if (!student || !sum) {
    return (
      <TeachingPage title="Student not found" breadcrumb={breadcrumbOf(A)}>
        <Link href={teachHref('/teaching', { a, class: cls })} class="text-[var(--color-accent)]">Back to the list</Link>
      </TeachingPage>
    );
  }

  const fb = A.feedbackFor(sum);
  const groups = A.groupsForStudent(student.id);
  // One row per mistake, listing every question it showed up in.
  const evidenced = Array.from(sum.results.filter((r) => r.misconceptions.length > 0).reduce((m, r) => {
    for (const c of r.misconceptions) {
      const e = m.get(c) ?? { code: c, items: [] as string[], evidence: [] as string[] };
      if (!e.items.includes(r.itemId)) e.items.push(r.itemId);
      if (r.evidence && !e.evidence.includes(r.evidence)) e.evidence.push(r.evidence);
      m.set(c, e);
    }
    return m;
  }, new Map<string, { code: string; items: string[]; evidence: string[] }>()).values());

  return (
    <TeachingPage title={student.name} breadcrumb={`${student.cls} · ${breadcrumbOf(A)}`} banner={<ProvisionalBanner A={A} />} actions={<StudentTabs studentId={student.id} a={a} active="test" />}>
      <Link href={teachHref('/teaching', { a, class: cls })} class="inline-flex items-center gap-1 text-[12px] text-[var(--color-text-muted)] hover:text-[var(--color-text)] mb-3">
        <ArrowLeft size={13} /> Back
      </Link>

      <div class="flex flex-wrap items-center gap-2 mb-5">
        <StatusChip status={student.status} />
        {student.status === 'completed' && (
          <>
            <PerfChip rate={sum.validCount ? sum.masteredCount / sum.validCount : null}>{sum.masteredCount}/{sum.validCount} mastered</PerfChip>
            <span class="text-[12px] text-[var(--color-text-muted)] tabular-nums">
              {sum.marks ?? '-'}/{sum.maxOfValid ?? '-'} marks ({pct(sum.attainment)}){!sum.complete && ' · partial paper'}
            </span>
          </>
        )}
      </div>

      {student.status === 'completed' && (
        <div class="flex flex-wrap items-center gap-2 mb-5 text-[12.5px] text-[var(--color-text-muted)]" data-myp-level>
          <span>MYP Criterion {A.assessment.criterion}:</span>
          {student.myp && !student.myp.needsReview && <><b class="text-[16px] text-[var(--color-text)] tabular-nums">{student.myp.level}</b><span class="text-[var(--color-text-faint)]">(confirmed by you)</span></>}
          {student.myp?.needsReview && <><s class="text-[var(--color-text)] tabular-nums">{student.myp.level}</s><ReviewChip /></>}
          {!student.myp && student.mypDraft !== undefined && <><span class="text-[var(--color-text)] tabular-nums">{student.mypDraft}</span><DraftChip label="Draft, not confirmed" /></>}
          {student.notAssessable && <><span class="text-[var(--color-text)]">Not assessable</span><span class="text-[var(--color-text-faint)]">({student.notAssessable.reason})</span></>}
          {!student.myp && !student.notAssessable && student.mypDraft === undefined && <span class="text-[var(--color-text-faint)]">no level confirmed yet</span>}
        </div>
      )}

      {student.status !== 'completed' ? (
        <Card title="Awaiting evidence">
          <div class="text-[13px] text-[var(--color-text-muted)]">
            {student.status === 'absent' ? 'Absent on the day.' : 'Catch-up sitting pending.'} No marks, feedback or level until the paper is in. This is not a zero.
          </div>
        </Card>
      ) : (
        <div class="grid lg:grid-cols-[1.1fr_1fr] gap-3">
          <div class="flex flex-col gap-3 min-w-0">
            {fb && (
              <Card title="Feedback" right={fb.needsReview ? <ReviewChip /> : fb.draft ? <DraftChip label="Draft, not approved" /> : undefined}
                sub={fb.needsReview ? 'Saved feedback needs re-checking against the latest marks' : fb.draft ? 'Not approved yet; not shared with the student' : 'Approved by you'}>
                <dl class="space-y-2.5 text-[13px]">
                  {([['Strength', fb.strength], ['Improve', fb.improvement], ['Next step', fb.nextStep], ['MYP target', fb.target]] as const).map(([k, v]) => (
                    <div key={k}>
                      <dt class="text-[10.5px] uppercase tracking-wider text-[var(--color-text-faint)]">{k}</dt>
                      <dd class="text-[var(--color-text)] leading-relaxed">{v}</dd>
                    </div>
                  ))}
                </dl>
              </Card>
            )}

            <Card title="What to do" sub="Every action this student is part of">
              {groups.length === 0 ? (
                <div class="text-[13px] text-[var(--color-text-muted)]">On track. No intervention needed.</div>
              ) : (
                <div class="flex flex-col gap-1.5">
                  {groups.map((g) => (
                    <Link key={g.kind + g.title} href={teachHref('/teaching/groups', { a, class: g.cls })} class="flex items-center gap-2 text-[13px] hover:underline">
                      <span class="text-[10.5px] uppercase tracking-wider text-[var(--color-text-faint)] w-[92px] shrink-0">{GROUP_LABEL[g.kind]}</span>
                      <span class="text-[var(--color-text)]">{g.title}{g.code && <CodeTag code={g.code} />}</span>
                    </Link>
                  ))}
                </div>
              )}
            </Card>

            {evidenced.length > 0 && (
              <Card title="Mistakes" sub="Only where the written answer shows it">
                <div class="flex flex-col gap-2.5">
                  {evidenced.map((r) => (
                    <div key={r.code} class="text-[13px]">
                      <div><span class="text-[var(--color-text)] font-medium">{A.meaning[r.code]}</span><CodeTag code={r.code} /> <span class="text-[11px] text-[var(--color-text-faint)]">in {r.items.join(', ')}</span></div>
                      {r.evidence.map((e) => <div key={e} class="text-[12px] italic text-[var(--color-text-muted)] mt-0.5">{e}</div>)}
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>

          <div class="flex flex-col gap-3 min-w-0">
            <Card title="Question by question" sub="Green = reached the pre-set mastery mark">
              <div class="flex flex-col">
                {A.items.map((item) => {
                  const r = sum.results.find((x) => x.itemId === item.id);
                  if (!r) return null;
                  const m = A.mastered(r);
                  return (
                    <div key={item.id} class="flex items-center gap-2 py-1.5 border-b border-[var(--color-border)] last:border-0">
                      <span class="w-8 text-[11.5px] font-medium text-[var(--color-text-muted)]">{item.id}</span>
                      <div class="flex-1 min-w-0">
                        <div class="text-[12.5px] text-[var(--color-text)] truncate" title={item.prompt}>{item.prompt}</div>
                        <div class="text-[10.5px] text-[var(--color-text-faint)]">{item.topic} · {item.command} · need {item.threshold}/{item.max}</div>
                      </div>
                      {r.notMarked ? (
                        <span class="text-[10.5px] px-1.5 py-0.5 rounded bg-[var(--color-elevated)] text-[var(--color-text-faint)]">– not marked</span>
                      ) : r.mark === null ? (
                        <span class="text-[10.5px] px-1.5 py-0.5 rounded border border-dashed border-[var(--color-border-strong)] text-[var(--color-text-faint)]">{r.unavailableReason ?? 'no evidence'}</span>
                      ) : (
                        <span class="text-[11.5px] font-medium tabular-nums px-1.5 py-0.5 rounded min-w-[40px] text-center" style={{ background: perfFill(m ? 1 : 0), color: INK }}>
                          {r.mark}/{item.max}{r.blank ? ' · blank' : ''}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </Card>

            <Card title="By topic" sub="Mastered / questions with evidence">
              {Object.entries(sum.topics).map(([t, r]) => (
                <RateBar key={t} label={t} rate={r.rate} n={r.n} mastered={r.mastered} minN={1} />
              ))}
            </Card>
          </div>
        </div>
      )}
      <div class="h-6" />
    </TeachingPage>
  );
}
